import type { NpcHullTier, StarSystem } from "./types";

// Tier baseline reuses 16.1's existing reward-tier CR values directly (no
// new pricing invented) -- these are the T1-T4 rows of that same table.
const TIER_BASELINE_CR: Record<NpcHullTier, number> = {
  T1: 150,
  T2: 330,
  T3: 726,
  T4: 1597
};

export type DangerTier = 0 | 1 | 2 | 3 | 4;

// Route danger multiplier, reused directly from 16.1's reward multiplier
// table, applied at the highest danger tier any system on the route touches.
const DANGER_TIER_MULTIPLIER: Record<DangerTier, number> = {
  0: 0.5,
  1: 1.0,
  2: 2.2,
  3: 4.84,
  4: 10.65
};

export interface HaulingRoute {
  path: string[]; // system IDs, origin to destination inclusive
  hops: number;
  maxDangerTier: DangerTier;
}

// Shortest-path hop count between two systems, computed from the actual
// locked galaxy graph (16.26) -- an unweighted BFS over each system's own
// `connections` list. Verified against all 4 of 16.26's worked-example
// routes (hops and max danger tier both match exactly).
export function shortestRoute(
  originId: string,
  destinationId: string,
  systems: StarSystem[]
): HaulingRoute {
  const byId = new Map(systems.map((s) => [s.id, s]));
  if (!byId.has(originId) || !byId.has(destinationId)) {
    throw new Error(`Unknown system in route: "${originId}" -> "${destinationId}"`);
  }

  const visited = new Set([originId]);
  const parent = new Map<string, string>();
  const queue: string[] = [originId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === destinationId) {
      break;
    }
    for (const next of byId.get(current)!.connections) {
      if (!visited.has(next)) {
        visited.add(next);
        parent.set(next, current);
        queue.push(next);
      }
    }
  }

  if (!visited.has(destinationId)) {
    throw new Error(`No route found from "${originId}" to "${destinationId}"`);
  }

  const path = [destinationId];
  let current = destinationId;
  while (current !== originId) {
    current = parent.get(current)!;
    path.unshift(current);
  }

  const maxDangerTier = Math.max(...path.map((id) => byId.get(id)!.dangerTier)) as DangerTier;

  return { path, hops: path.length - 1, maxDangerTier };
}

// Danger-weighted alternative to shortestRoute (16.47) -- Section 6's Space
// Map wireframe names three route variants (fewest hops / most
// fuel-efficient / safest); the fuel-efficient variant was CUT from the
// design in 16.76 and is not coming back -- "safest" is real, built on
// per-hop danger data this codebase already has. Minimizes the
// highest danger tier touched anywhere along the route, tie-broken by
// fewest hops -- not total/average danger, since one bad system on an
// otherwise-safe route is what actually threatens a hauler, not a long
// safe detour. O(V^2) is deliberate over a heap: 107 systems total, no
// need for the complexity.
export function safestRoute(originId: string, destinationId: string, systems: StarSystem[]): HaulingRoute {
  const byId = new Map(systems.map((s) => [s.id, s]));
  if (!byId.has(originId) || !byId.has(destinationId)) {
    throw new Error(`Unknown system in route: "${originId}" -> "${destinationId}"`);
  }

  const best = new Map<string, { maxTier: number; hops: number }>();
  const parent = new Map<string, string>();
  best.set(originId, { maxTier: byId.get(originId)!.dangerTier, hops: 0 });
  const visited = new Set<string>();

  const better = (a: { maxTier: number; hops: number }, b: { maxTier: number; hops: number }): boolean =>
    a.maxTier < b.maxTier || (a.maxTier === b.maxTier && a.hops < b.hops);

  while (true) {
    let currentId: string | null = null;
    let currentBest: { maxTier: number; hops: number } | null = null;
    best.forEach((b, id) => {
      if (visited.has(id)) return;
      if (!currentBest || better(b, currentBest)) {
        currentId = id;
        currentBest = b;
      }
    });
    if (currentId === null || currentId === destinationId) break;
    visited.add(currentId);
    const current = byId.get(currentId)!;
    current.connections.forEach((nextId) => {
      const next = byId.get(nextId);
      if (!next) return;
      const candidate = { maxTier: Math.max(currentBest!.maxTier, next.dangerTier), hops: currentBest!.hops + 1 };
      const existing = best.get(nextId);
      if (!existing || better(candidate, existing)) {
        best.set(nextId, candidate);
        parent.set(nextId, currentId!);
      }
    });
  }

  if (!best.has(destinationId)) {
    throw new Error(`No route found from "${originId}" to "${destinationId}"`);
  }

  const path = [destinationId];
  let cur = destinationId;
  while (cur !== originId) {
    cur = parent.get(cur)!;
    path.unshift(cur);
  }

  return { path, hops: path.length - 1, maxDangerTier: best.get(destinationId)!.maxTier as DangerTier };
}

export interface HaulingMission {
  originSystemId: string;
  destinationSystemId: string;
  hullTier: NpcHullTier;
  hops: number;
  maxDangerTier: DangerTier;
  // Excludes the time bonus (16.26: "roughly 0.8x-1.2x, exact curve TBD" --
  // blocked on a locked ship-speed/per-hop-travel-time stat that doesn't
  // exist yet anywhere in the doc, so it isn't invented here). Matches
  // 16.26's own worked-example rewards, which are likewise shown "excl.
  // time bonus."
  rewardCr: number;
}

// Reward = (tier baseline x 0.3) x hops x route danger multiplier (16.26).
export function generateHaulingMission(
  origin: StarSystem,
  destination: StarSystem,
  hullTier: NpcHullTier,
  systems: StarSystem[]
): HaulingMission {
  const route = shortestRoute(origin.id, destination.id, systems);
  const rewardCr = Math.round(
    TIER_BASELINE_CR[hullTier] * 0.3 * route.hops * DANGER_TIER_MULTIPLIER[route.maxDangerTier]
  );

  return {
    originSystemId: origin.id,
    destinationSystemId: destination.id,
    hullTier,
    hops: route.hops,
    maxDangerTier: route.maxDangerTier,
    rewardCr
  };
}
